import { TransportRequest, createTransport } from '@sentry/core';
import * as Sentry from '@sentry/react';

import { router } from '@/services/router/router';
import { hasAccessToken, useUserStore } from '@/stores/user';
import { environment } from '@/utils/environment';

/**
 * When a test is being run via cypress, `?cypress=true` is added to the URL.
 * This is stored in sessionStorage in `index.html` to avoid exceeding our Sentry usage limits.
 */
const isCypress = sessionStorage.getItem('cypress');

// Set to true to enable sentry locally.
const ENABLE_SENTRY_LOCALLY = false;

export class SentryManager {
  private instance: ReturnType<typeof Sentry.init>;
  private isUsingTunnel?: boolean;
  public isEnabled?: boolean;

  constructor() {
    /**
     * When there are login credentials present, initialize sentry with the tunnel enabled.
     */
    const useTunnel = hasAccessToken();

    /**
     * Check if we should use sentry based on the hostname and cypress
     */
    const hostname = window.location.hostname;

    const allowedHostnames = [
      'ont.digi-v.nl',
      'int.digi-v.nl',
      'acc.digi-v.nl',
      'pre.digi-v.nl',
      'digi-v.nl',
    ];

    if (ENABLE_SENTRY_LOCALLY) {
      allowedHostnames.push('local.digi-v.nl');
    }

    this.isEnabled = allowedHostnames.includes(hostname) && !isCypress;

    /**
     * Iinitialise if we should use sentry
     */
    if (this.isEnabled) {
      this.init(useTunnel);
      this.subscribe();
    }
  }

  /**
   * Subscribes to the user store to re initialize the Sentry instance when the user logs in or out.
   */
  subscribe() {
    useUserStore.subscribe((state, prevState) => {
      if (state.isLoggedIn === prevState.isLoggedIn) return;

      if (state.isLoggedIn && !this.isUsingTunnel) {
        this.reInit(true);
      }

      if (!state.isLoggedIn && this.isUsingTunnel) {
        this.reInit(false);
      }
    });
  }

  /**
   * Initializes the Sentry instance.
   */
  init(useTunnel: boolean) {
    this.isUsingTunnel = useTunnel;

    const tunnelUrl = `${environment.getAPIUrl()}/oth/sentry_tunnel`;

    this.instance = Sentry.init({
      dsn: this.getDSN(),
      tunnel: useTunnel ? tunnelUrl : undefined,
      transport: useTunnel ? this.makeFetchTransport : undefined,

      integrations: [
        // Captures errors on failed requests from Fetch and XHR
        Sentry.httpClientIntegration({
          failedRequestStatusCodes: [[400, 599]],
        }),
        // Integrates tanstack router browser tracing
        Sentry.tanstackRouterBrowserTracingIntegration(router),
      ],

      // Setting a sample rate is required for sending performance data.
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control.
      tracesSampleRate: 1.0,
    });
  }

  /**
   * Re initializes the Sentry instance.
   */
  reInit(useTunnel: boolean) {
    this.instance?.close();
    this.init(useTunnel);
  }

  /**
   * When the tunnel is being used, the API requires the login credentials to be added to the request.
   */
  makeFetchTransport(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options: any
  ) {
    function makeRequest(request: TransportRequest) {
      const access_token = localStorage.getItem('__AADV_access_token__');
      const { fetchOptions, url } = options;

      const requestOptions: RequestInit = {
        body: request.body,
        method: 'POST',
        referrerPolicy: 'origin',
        // Include credentials for the request
        credentials: access_token ? 'include' : undefined,
        headers: {
          ...(fetchOptions?.headers || {}),
        },
        ...fetchOptions,
      };

      // If an access token is present when logging in via the old method, add it to the request headers
      if (access_token) {
        if (!requestOptions.headers) {
          requestOptions.headers = {};
        }

        (requestOptions.headers as Record<string, string>).Authorization =
          `Bearer ${access_token}`;
      }

      return fetch(url, requestOptions).then((response) => {
        return {
          statusCode: response.status,
          headers: {
            'x-sentry-rate-limits': response.headers.get(
              'X-Sentry-Rate-Limits'
            ),
            'retry-after': response.headers.get('Retry-After'),
          },
        };
      });
    }

    return createTransport(options, makeRequest);
  }

  /**
   * Returns the DSN for the current hostname.
   */
  private getDSN() {
    const hostname = window.location.hostname;

    switch (hostname) {
      case 'ont.digi-v.nl':
        return 'https://6e161a5710280f70a05dc4f0ce15937d@o4508381609590784.ingest.de.sentry.io/4508381617717328';
      case 'int.digi-v.nl':
        return 'https://afd579ee4375954d0cdb06e238999163@o4508381609590784.ingest.de.sentry.io/4508398138949712';
      case 'acc.digi-v.nl':
        return 'https://45e14f9a990ff13f243048ddc5fd3be2@o4508381609590784.ingest.de.sentry.io/4510900681965648';
      case 'pre.digi-v.nl':
        return 'https://d76f2d341b476d9f38bc8c6c7584e298@o4508381609590784.ingest.de.sentry.io/4509435488239696';
      case 'digi-v.nl':
        return 'https://919a9fe644045de46128abeab228732a@o4508381609590784.ingest.de.sentry.io/4509354464837712';
      case 'local.digi-v.nl':
        return ENABLE_SENTRY_LOCALLY
          ? 'https://6e161a5710280f70a05dc4f0ce15937d@o4508381609590784.ingest.de.sentry.io/4508381617717328'
          : '';
      default:
        return '';
    }
  }
}
